import {
  PropertyRow,
  Box,
  Select,
  SelectItem,
  SelectPopup,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Text,
  type Color,
  type Omittable,
} from "@salestrak/ui";
import { DateText } from "#app/components/date-text";
import { type AgeStatus } from "#app/lib/age-status";
import { NONE_OPTION_LABEL } from "#app/lib/custom-field-utils";
import { isIssueAgeStatus } from "#app/lib/data";
import { useMinuteKey } from "#app/lib/hooks/use-date-boundary-key";
import {
  cycleKey,
  effectiveProjectCycle,
  effectiveStageCycle,
  findFollowUpCyclePreset,
  FOLLOW_UP_CYCLE_PRESETS,
  formatNextUpdate,
  formatUpdateCycle,
  liveAgeStatus,
  nextUpdateDue,
  type ProjectAge,
  type StagePin,
  type UpdateCycle,
  type WorkflowCycle,
} from "#app/lib/project-updates";

const DEFAULT_VALUE = "__default__";

/** The one color the clock may wear: yellow while Update soon, red once Stale,
 *  none while healthy or off — green is reserved for the moment of posting. */
const STATUS_COLOR: Partial<Record<AgeStatus, Color>> = {
  atRisk: "yellow",
  offTrack: "red",
};

export type ProjectClockReading = Omittable<{
  updatesOff: boolean;
  /** The Cycle row's value — the cadence, or "Off". */
  cycleLabel: string;
  /** The Next row's string: "in 3 days", "19 minutes overdue", or the empty
   *  marker while off. The same string the folded header captions. */
  nextLabel: string;
  color?: Color;
}>;

/**
 * The Project clock as the reader should see it right now, derived from the
 * last posted Update and the effective cycle. Minute-granular, so a section
 * left open rolls "in 1 minute" over to "1 minute overdue" without a refetch.
 * Shared by the section body and its folded caption so the two can never
 * disagree.
 */
export function useProjectClock(
  age: ProjectAge | null | undefined,
  stage: StagePin | null | undefined,
  workflow: WorkflowCycle | null | undefined,
): ProjectClockReading {
  void useMinuteKey();
  const effective = effectiveProjectCycle(age, stage, workflow);
  const updatesOff = effective.cycle == null;
  const storedStatus = age?.status;
  // The live status wins; the stored column is the fallback while the live
  // one cannot be derived (no Update posted yet). Off is its own state.
  const statusKey: AgeStatus = updatesOff
    ? "disabled"
    : (liveAgeStatus(age, stage, workflow) ??
      (isIssueAgeStatus(storedStatus) ? storedStatus : "onTrack"));
  return {
    updatesOff,
    cycleLabel: updatesOff ? "Off" : formatUpdateCycle(effective.cycle),
    nextLabel: updatesOff
      ? NONE_OPTION_LABEL
      : formatNextUpdate(nextUpdateDue(age, stage, workflow)),
    color: STATUS_COLOR[statusKey],
  };
}

export type ProjectUpdateWidgetProps = Omittable<{
  age?: ProjectAge | null;
  stage?: StagePin | null;
  workflow?: WorkflowCycle | null;
  editable: boolean;
  onCycleChange: (cycle: UpdateCycle) => void;
  onUseDefault: () => void;
}>;

/**
 * The Follow up section body for a RUNNING Project clock, on the summary
 * stack's row grammar: Cycle, Last, Next — the same three rows an Account or
 * Contact section reads, minus their Reason. One string carries the state
 * (the Next row's), in at most one color; Last is a plain date. Updates off
 * is not this body's state: the module renders the Start Follow up action row
 * instead (`issue-updates/index.tsx`).
 *
 * Relies on the summary stack's section chrome. Do not add a second painted
 * card around it: that nested surface makes this one section heavier than
 * Properties and Custom fields.
 */
export function ProjectUpdateWidget({
  age,
  stage,
  workflow,
  editable,
  onCycleChange,
  onUseDefault,
}: ProjectUpdateWidgetProps) {
  const clock = useProjectClock(age, stage, workflow);
  const effective = effectiveProjectCycle(age, stage, workflow);
  const inherited = effectiveStageCycle(stage, workflow);

  // The Project's own cycle, or following the rung below. A stored non-preset
  // cycle (an automation's) has no row, so the trigger falls back to its
  // computed label and the only ways out are a preset or the default.
  const selectedValue =
    effective.source === "project" && effective.cycle
      ? cycleKey(effective.cycle)
      : DEFAULT_VALUE;

  // The picker is open to every editor no matter what the Stage pins — an
  // Admin pin is only the default; the rep sets the cycle they want on their
  // own Project. Nothing in this list turns the Follow up OFF: the inherit
  // row is offered only while the rung below carries a cycle, and ending is
  // the section menu's own act.
  const cycleControl = editable ? (
    <Select
      value={selectedValue}
      onValueChange={(next: string | null) => {
        if (!next) return;
        if (next === DEFAULT_VALUE) {
          onUseDefault();
          return;
        }
        const preset = findFollowUpCyclePreset(next);
        if (preset) onCycleChange(preset);
      }}
    >
      {/* The family Cycle grammar: a pre-answered fact on the row, not a field
          asking — value + single chevron, no box, same as the Account/Contact
          sections. `bleedStart` keeps the value in column with the flush
          Last/Next values below; the hover wash overhangs the row edge. */}
      <SelectTrigger
        aria-label="Change cycle"
        size="sm"
        variant="ghost"
        width="auto"
        bleedStart
        chevronIcon="chevron-down"
      >
        {/* Computed rather than resolved from `items`: the inherit row carries
            the inherited cadence as a suffix the trigger has no room for, and
            a stored non-preset cycle has no row at all. */}
        <SelectValue>{clock.cycleLabel}</SelectValue>
      </SelectTrigger>
      <SelectPopup aria-label="Cycles">
        {FOLLOW_UP_CYCLE_PRESETS.map((cycle) => (
          <SelectItem key={cycleKey(cycle)} value={cycleKey(cycle)}>
            {formatUpdateCycle(cycle)}
          </SelectItem>
        ))}
        {inherited.cycle ? (
          <>
            <SelectSeparator />
            <SelectItem value={DEFAULT_VALUE}>
              <Box direction="row" align="center" gap="xs" minWidth={0}>
                <Text size="sm">
                  {inherited.source === "stage" ? "Use Stage default" : "Use Workflow default"}
                </Text>
                <Text size="sm" variant="muted" truncate={1}>
                  · {formatUpdateCycle(inherited.cycle)}
                </Text>
              </Box>
            </SelectItem>
          </>
        ) : null}
      </SelectPopup>
    </Select>
  ) : (
    <Text size="sm">{clock.cycleLabel}</Text>
  );

  return (
    <Box gap={6}>
      <PropertyRow icon="hourglass" label="Cycle">
        {cycleControl}
      </PropertyRow>
      <PropertyRow icon="history" label="Last">
        {/* A plain date, the "Last" every section in the family reads: the
            state rides the Next row, so this one wears no tint. */}
        <DateText
          date={age?.date}
          format="long"
          size="sm"
          variant="default"
          truncate={false}
        />
      </PropertyRow>
      <PropertyRow icon="calendar-clock" label="Next">
        <Text size="sm" color={clock.color}>
          {clock.nextLabel}
        </Text>
      </PropertyRow>
    </Box>
  );
}
